import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { AlertTriangle, Refresh, ToggleLeft, ToggleRight } from "@/components/icons";

import {
  fetchLogs,
  type LogEntry,
  type LogTelemetry,
  type LogsResponse,
  type LogsStorage,
  type LogsWriterStatus,
} from "@/lib/api";
import { HttpError } from "@/lib/http";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DataTable } from "@/components/ui/data-table";
import { Card, CardContent } from "@/components/ui/card";
import { DataTableShell } from "@/components/ui/data-table-shell";
import { formatDuration, formatNumber } from "@voltie/localization-web";
import { useCatalog, type Translate } from "@/app/localization";
import { viewerInstant } from "@/app/displayFormat";

type ViewMode = "decoded" | "raw";

// Mirrors MAX_LOG_LIMIT in web/diagnostics_reader.py.
const MAX_LIMIT = 10_000;
const DEFAULT_LIMIT = 500;

// Fixed writer reason codes of web/diagnostics_reader.py, each a case of operator.ops.logs.writer_reason.
const WRITER_REASONS: ReadonlySet<string> = new Set([
  "disabled_by_config",
  "heartbeat_missing",
  "heartbeat_stale",
  "heartbeat_malformed",
  "redis_unavailable",
  "writes_failing",
  "store_config_rejected",
]);

function queryErrorText(t: Translate, code: unknown): string | undefined {
  if (code === "invalid_limit") return t("operator.ops.logs.invalid_limit", { max: formatNumber(MAX_LIMIT) });
  if (code === "invalid_unit_id") return t("operator.ops.logs.invalid_unit_id");
  return undefined;
}

function writerReasonText(t: Translate, code: unknown): string {
  // A known code only: anything else (including `__proto__` or `toString`) reaches the select's generic `other`.
  return t("operator.ops.logs.writer_reason", { reason: typeof code === "string" && WRITER_REASONS.has(code) ? code : "" });
}

/** Server timestamps without a zone are server UTC, not browser-local time. */
function parseServerTime(value: unknown): Date | null {
  if (typeof value !== "string" || !value) return null;
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(value);
  const d = new Date(hasZone ? value : `${value}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatTimestamp(entry: LogEntry): string {
  const d = parseServerTime(entry.timestamp);
  if (d) return viewerInstant(d.toISOString(), "dateTimeSeconds") ?? "—";
  return typeof entry.time === "string" && entry.time ? entry.time : "—";
}

function formatAge(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

/** Nested `telemetry` first, then the flat shape of older entries. Zero is a value. */
function telemetry(entry: LogEntry, key: keyof LogTelemetry): unknown {
  return entry.telemetry?.[key] ?? entry[key] ?? null;
}

function numeric(entry: LogEntry, key: keyof LogTelemetry): number | null {
  const v = telemetry(entry, key);
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function formatReading(n: number | null, digits: number, suffix = ""): string {
  return n == null ? "—" : `${formatNumber(n, digits)}${suffix}`;
}

/** The decoded server command; day, time and currents are shown as the charger sent them. */
function formatCommand(t: Translate, entry: LogEntry): string {
  const cmd = entry.command_decoded;
  if (cmd && cmd.day) {
    return t("operator.ops.logs.decoded_command", {
      when: `${cmd.day} ${cmd.time || ""}`.trim(),
      online: String(cmd.online_current ?? "—"),
      offline: String(cmd.offline_current ?? "—"),
      rapid: cmd.rapid_mode ? "on" : "off",
    });
  }
  return rawCommand(entry);
}

function rawCommand(entry: LogEntry): string {
  const cmd = entry.server_command ?? entry.command;
  return typeof cmd === "string" && cmd ? cmd : "—";
}

function timestampColumn(t: Translate): ColumnDef<LogEntry, unknown> {
  return {
    accessorKey: "timestamp",
    header: t("operator.ops.logs.timestamp"),
    cell: ({ row }) => (
      <span className="font-mono text-xs whitespace-nowrap">{formatTimestamp(row.original)}</span>
    ),
  };
}

function unitColumn(t: Translate): ColumnDef<LogEntry, unknown> {
  return {
    accessorKey: "unit_id",
    header: t("operator.units.column_unit_id"),
    cell: ({ row }) => <span className="font-mono text-xs">{row.original.unit_id || "—"}</span>,
  };
}

function numericColumn(
  key: keyof LogTelemetry,
  header: string,
  format: (n: number | null) => string,
): ColumnDef<LogEntry, unknown> {
  return { id: key, header, cell: ({ row }) => format(numeric(row.original, key)) };
}

function decodedColumns(t: Translate): ColumnDef<LogEntry, unknown>[] {
  return [
  timestampColumn(t),
  unitColumn(t),
  {
    id: "state",
    header: t("operator.ops.logs.state"),
    cell: ({ row }) => {
      const state = telemetry(row.original, "state_name") ?? row.original.state;
      return typeof state === "string" && state ? state : "—";
    },
  },
  numericColumn("voltage", t("operator.metric.voltage"), (n) => formatReading(n, 1, "V")),
  numericColumn("current", t("operator.metric.current"), (n) => formatReading(n, 1, "A")),
  numericColumn("frequency", t("operator.ops.logs.frequency"), (n) => formatReading(n, 1)),
  numericColumn("power_factor", t("operator.metric.power_factor_short"), (n) => formatReading(n, 2)),
  numericColumn("session_energy", t("operator.ops.logs.energy"), (n) => formatReading(n == null ? null : n / 1000, 2, " kWh")),
  numericColumn("temperature", t("operator.ops.logs.temperature"), (n) => (n == null ? "—" : `${n}°C`)),
  numericColumn("wifi_rssi", t("operator.ops.logs.rssi"), (n) => (n == null ? "—" : `${n}dBm`)),
  {
    id: "command",
    header: t("operator.ops.logs.command"),
    cell: ({ row }) => {
      const text = formatCommand(t, row.original);
      return <span className="text-xs max-w-[320px] truncate block" title={text}>{text}</span>;
    },
  },
  ];
}

function rawColumns(t: Translate): ColumnDef<LogEntry, unknown>[] {
  return [
  timestampColumn(t),
  unitColumn(t),
  {
    id: "raw_message",
    header: t("operator.ops.logs.raw_message"),
    cell: ({ row }) => {
      const raw = row.original.raw_message ?? row.original.message;
      return (
        <span className="font-mono text-xs max-w-md truncate block">
          {typeof raw === "string" && raw ? raw : "—"}
        </span>
      );
    },
  },
  {
    id: "command",
    header: t("operator.ops.logs.command"),
    cell: ({ row }) => <span className="text-xs">{rawCommand(row.original)}</span>,
  },
  ];
}

function formatCounter(t: Translate, n: number | null): string {
  return n == null ? t("operator.ops.logs.counter_unknown") : formatNumber(n);
}

function WriterStatus({ writer, newestAge, count }: {
  writer: LogsWriterStatus;
  newestAge: number | null | undefined;
  count: number;
}) {
  const { t } = useCatalog();
  const live = writer.enabled && writer.reason == null;
  const state = live
    ? "live"
    : !writer.enabled ? "disabled"
    : writer.reason === "writes_failing" ? "degraded"
    : "unavailable";
  // Reported only by writers that know their write state; older ones omit it.
  const reportsWrites = writer.write_error_active != null;
  const failures = writer.write_failures ?? null;
  const newest = newestAge != null
    ? t("operator.ops.logs.newest_entry", { state: "age", age: formatAge(newestAge) })
    : t("operator.ops.logs.newest_entry", { state: count > 0 ? "unknown" : "none", age: "" });
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground" data-testid="logs-writer-status">
      <Badge variant={live ? "success" : "warning"}>
        {t("operator.ops.logs.writer_state", { state })}
      </Badge>
      {writer.heartbeat_age_s != null && <span>{t("operator.ops.logs.heartbeat_ago", { age: formatAge(writer.heartbeat_age_s) })}</span>}
      {writer.enabled && (
        <span>
          {t("operator.ops.logs.writer_counters", {
            written: formatCounter(t, writer.entries_written),
            dropped: formatCounter(t, writer.dropped_entries),
          })}
          {reportsWrites && (
            <>
              {" • "}
              {failures == null
                ? t("operator.ops.logs.write_failures_unknown")
                : t("operator.ops.logs.write_failures", { count: failures })}
            </>
          )}
        </span>
      )}
      {writer.enabled && reportsWrites && (
        <span>
          {writer.last_write_ok_age_s != null
            ? t("operator.ops.logs.last_write_ago", { age: formatAge(writer.last_write_ok_age_s) })
            : t("operator.ops.logs.no_successful_write")}
        </span>
      )}
      <span>{newest}</span>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GiB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MiB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KiB`;
  return `${n} B`;
}

function StorageStatus({ storage }: { storage: LogsStorage }) {
  const { t } = useCatalog();
  const known = storage.metadata === "ok";
  const evicted = known ? storage.evicted_keys : null;
  const used = known ? storage.used_memory_bytes : null;
  const limit = known ? storage.maxmemory_bytes : null;
  // Only an explicit 0 means no limit; null is an unknown limit, not an unlimited one.
  const memory = used == null
    ? null
    : t("operator.ops.logs.memory", {
        limit: limit == null ? "unknown" : limit === 0 ? "none" : "known",
        used: formatBytes(used),
        max: limit ? formatBytes(limit) : "",
      });
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground" data-testid="logs-storage-status">
      <Badge variant="outline">
        {t("operator.ops.logs.storage_mode", { mode: storage.mode === "dedicated" ? "dedicated" : "live" })}
      </Badge>
      <Badge variant={evicted != null && evicted > 0 ? "warning" : "neutral"}>
        {t("operator.ops.logs.evicted_keys", { value: formatCounter(t, evicted) })}
      </Badge>
      {memory && <span>{memory}</span>}
      <span>{t("operator.ops.logs.storage_note", { metadata: known ? "ok" : "unavailable" })}</span>
    </div>
  );
}

function errorText(t: Translate, error: unknown): string {
  if (error instanceof HttpError) {
    const code = (error.body as { error?: unknown } | undefined)?.error;
    const known = error.status === 400 ? queryErrorText(t, code) : undefined;
    if (known) return known;
    return t("operator.ops.logs.load_failed_status", { status: String(error.status) });
  }
  return t("operator.ops.logs.load_failed");
}

export function LogsPage() {
  const { t, catalog } = useCatalog();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => ({ decoded: decodedColumns(t), raw: rawColumns(t) }), [catalog]);
  // Deep-link support: /logs?unit_id=XXX pre-fills the filter (used by the
  // "Logs" button on the unit detail page). The input stays editable — the
  // param only seeds the initial value.
  const [searchParams] = useSearchParams();
  const [unitId, setUnitId] = useState(() => searchParams.get("unit_id") ?? "");
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>("decoded");

  const q = useQuery({
    queryKey: ["logs", { unitId, limit }],
    queryFn: () => fetchLogs({ unit_id: unitId || undefined, limit }),
    refetchInterval: autoRefresh ? 5_000 : false,
  });

  // Older servers answered a bare array; current ones the LogsResponse envelope.
  const data: LogsResponse | LogEntry[] | undefined = q.data;
  const envelope = data && !Array.isArray(data) ? data : null;
  const entries: LogEntry[] = Array.isArray(data) ? data : Array.isArray(envelope?.logs) ? envelope.logs : [];
  const isStale = !!envelope?.stale;
  const writer = envelope?.writer ?? null;
  const writerDown = !!writer && (!writer.enabled || writer.reason != null);
  const storage = envelope?.storage ?? null;
  const storeRejected = !!storage?.config_error;

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t("operator.nav.logs")}</h1>
          <div className="text-sm opacity-70">{t("operator.ops.logs.subtitle")}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            title={autoRefresh ? t("operator.ops.logs.pause_auto_refresh") : t("operator.ops.logs.enable_auto_refresh")}
          >
            {autoRefresh ? <ToggleRight className="h-4 w-4 text-success" /> : <ToggleLeft className="h-4 w-4" />}
            {t("operator.ops.auto_toggle", { state: autoRefresh ? "on" : "off" })}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => q.refetch()}>
            <Refresh className="h-4 w-4" /> {t("common.action.refresh")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-[200px]">
              <Label>{t("operator.ops.logs.unit_optional")}</Label>
              <Input className="mt-1" placeholder={t("operator.ops.logs.unit_placeholder")} value={unitId} onChange={(e) => setUnitId(e.target.value)} />
            </div>
            <div className="w-32">
              <Label>{t("operator.ops.logs.limit")}</Label>
              <Input
                className="mt-1"
                type="number"
                min={1}
                max={MAX_LIMIT}
                value={String(limit)}
                onChange={(e) => {
                  const n = Math.trunc(Number(e.target.value));
                  setLimit(Number.isFinite(n) && n >= 1 ? Math.min(n, MAX_LIMIT) : DEFAULT_LIMIT);
                }}
              />
            </div>
            <ToggleGroup type="single" size="sm" variant="outline" value={viewMode} onValueChange={(v) => v && setViewMode(v as ViewMode)}>
              <ToggleGroupItem value="decoded">{t("operator.ops.logs.view_decoded")}</ToggleGroupItem>
              <ToggleGroupItem value="raw">{t("operator.ops.logs.view_raw")}</ToggleGroupItem>
            </ToggleGroup>
          </div>
        </CardContent>
      </Card>

      {isStale ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertDescription>
            {t("operator.ops.logs.store_unavailable", { state: storeRejected ? "rejected" : "unavailable" })}
          </AlertDescription>
        </Alert>
      ) : writerDown && writer ? (
        <Alert>
          <AlertTriangle />
          <AlertDescription>
            {writerReasonText(t, writer.reason)}
            {entries.length > 0 ? <> {t("operator.ops.logs.history_not_live")}</> : null}
          </AlertDescription>
        </Alert>
      ) : null}

      {writer && !q.isError && (
        <WriterStatus writer={writer} newestAge={envelope?.newest_entry_age_s} count={entries.length} />
      )}
      {storage && !q.isError && <StorageStatus storage={storage} />}
      {envelope && !q.isError && (
        <p className="text-xs text-muted-foreground" data-testid="logs-retention-note">
          {t("operator.ops.logs.retention_note")}
        </p>
      )}

      {/* Content */}
      {q.isError ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertDescription>{errorText(t, q.error)}</AlertDescription>
        </Alert>
      ) : (
        <DataTableShell>
          <DataTable
            columns={viewMode === "decoded" ? columns.decoded : columns.raw}
            data={entries}
            isLoading={q.isLoading}
            tableClassName="text-xs"
            emptyMessage={t("operator.ops.logs.empty")}
          />
        </DataTableShell>
      )}

      <div className="text-center text-xs opacity-50">
        {t("operator.ops.logs.footer", { count: entries.length, auto: autoRefresh ? "on" : "off", interval: formatDuration(5, "seconds") })}
      </div>
    </div>
  );
}
